import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useConsoleRole } from '../../session/SessionContext';
import { usePageTab } from '../../shell/Shell';
import { sx } from '../../ui/designStyles';
import { ReadOnlyNote } from '../../ui/ReadOnly';
import { SummaryHome } from './SummaryHome';
import { LiveDecisions } from './LiveDecisions';
import { TruthLedger } from './TruthLedger';
import { GovernedSystems } from './GovernedSystems';

export type OversightTab = 'summary' | 'live' | 'ledger' | 'systems';
const TABS: { key: Exclude<OversightTab, 'systems'>; name: string }[] = [
  { key: 'summary', name: 'Summary' },
  { key: 'live', name: 'Live Decisions' },
  { key: 'ledger', name: 'Truth Ledger' },
];
const TAB_LABEL: Record<OversightTab, string> = { summary: 'Summary', live: 'Live Decisions', ledger: 'Truth Ledger', systems: 'Governed Systems' };

// design monVals: monTabs
const tabStyle = (on: boolean) =>
  'display:inline-flex;align-items:center;min-height:36px;padding:0 2px;font-size:16px;font-weight:' + (on ? '700' : '600') + ';text-decoration:none;white-space:nowrap;margin-bottom:-1px;' +
  (on ? 'color:#22272E;box-shadow:inset 0 -2px 0 #2F5C97;' : 'color:#54606C;');

/** What another tab asked the Truth Ledger to open with. */
export interface LedgerIntent {
  exportOpen?: boolean;
  query?: string;
}

export function OversightPage() {
  const { role } = useConsoleRole();
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  // V9: Oversight opens on Live Decisions (design monTab default; the side menu too).
  const tab: OversightTab = raw === 'summary' || raw === 'ledger' || raw === 'systems' ? raw : 'live';
  const sub = params.get('sub') === 'audit' ? 'audit' : 'chain';
  const [intent, setIntent] = useState<LedgerIntent>({});
  usePageTab(tab === 'ledger' && sub === 'audit' ? 'Security audit' : TAB_LABEL[tab]);

  const go = useCallback(
    (t: OversightTab, next: LedgerIntent = {}, s?: 'chain' | 'audit') => {
      setIntent(next);
      const p = new URLSearchParams();
      if (t !== 'live') p.set('tab', t);
      if (t === 'ledger' && s === 'audit') p.set('sub', 'audit');
      setParams(p, { replace: false });
    },
    [setParams],
  );
  const setSub = useCallback((s: 'chain' | 'audit') => go('ledger', {}, s), [go]);

  if (!role) return null;
  return (
    <div data-testid="oversight" data-screen-label="Monitoring" style={sx('display:flex;flex-direction:column;gap:11px;min-width:0;flex:1 1 auto;min-height:0;height:100%;overflow:hidden;')}>
      <ReadOnlyNote />
      <div role="tablist" aria-label="Oversight" style={sx('flex:0 0 auto;display:flex;align-items:center;gap:6px 22px;flex-wrap:wrap;min-width:0;border-bottom:1px solid rgba(34,39,46,.12);')}>
        {TABS.map((t) => {
          const on = t.key === tab;
          return (
            <button key={t.key} type="button" role="tab" aria-selected={on} className="dtab" style={sx(tabStyle(on))} onClick={() => go(t.key)}>
              {t.name}
            </button>
          );
        })}
      </div>
      <div
        style={sx(
          tab === 'summary'
            ? 'flex:1 1 auto;min-height:0;overflow-y:auto;overflow-x:hidden;min-width:0;display:flex;flex-direction:column;'
            : 'flex:1 1 auto;min-height:0;overflow-y:auto;overflow-x:auto;min-width:0;display:flex;flex-direction:column;gap:10px;padding-right:3px;',
        )}
      >
        {tab === 'summary' && <SummaryHome onGo={go} />}
        {tab === 'live' && <LiveDecisions onGo={go} />}
        {tab === 'ledger' && <TruthLedger sub={sub} onSub={setSub} intent={intent} onIntentUsed={() => setIntent({})} />}
        {tab === 'systems' && <GovernedSystems onBack={() => go('summary')} />}
      </div>
    </div>
  );
}
